/* holster-finder-v2.js — Phase 2: data layer added */
/* Phases 3-8 will extend this module in place. */

(function () {
  'use strict';

  // Shared SVG icon helper — stroke-based Feather-style icons, consistent weight
  function hfIcon(paths) {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths + '</svg>';
  }

  /* ------------------------------------------------------------------
     MODULE ROOT
     ------------------------------------------------------------------ */
  const HF = {};
  window.HF = HF; // expose for console verification

  /* ------------------------------------------------------------------
     STATE
     ------------------------------------------------------------------ */
  HF.state = {
    branch: null,
    answers: {},
    history: [],
    candidates: [],
  };

  /* ------------------------------------------------------------------
     DATA LAYER — CONSTANTS
     ------------------------------------------------------------------ */

  // Bold SCPO API store domain. If fetches return 404, confirm this matches
  // the myShopify domain in Shopify Admin > Settings > Store details.
  HF.BOLD_STORE = 't1cstore.myshopify.com';

  /* ------------------------------------------------------------------
     DATA LAYER — DICTIONARY
     ------------------------------------------------------------------ */

  HF.dictionary = null;

  HF.loadDictionary = async function () {
    if (HF.dictionary) return HF.dictionary;
    const url = window.HOLSTER_FINDER_DICTIONARY_URL;
    if (!url) {
      console.error('[holster-finder] HOLSTER_FINDER_DICTIONARY_URL not set');
      HF.renderErrorState("We're having trouble loading the finder. Try again or browse all holsters.");
      return null;
    }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Dictionary fetch failed: ' + res.status);
      HF.dictionary = await res.json();
      return HF.dictionary;
    } catch (err) {
      console.error('[holster-finder] dictionary load failed', err);
      HF.renderErrorState("We're having trouble loading the finder. Try again or browse all holsters.");
      return null;
    }
  };

  /* ------------------------------------------------------------------
     DATA LAYER — BOLD API FETCH
     ------------------------------------------------------------------ */

  // Bold responses are cached for the browser session. If T1C admin updates a
  // product mid-session the user will see stale data until next page load -
  // acceptable given how rarely Bold configs change during business hours.
  HF.boldCache = {};

  /* ------------------------------------------------------------------
     DATA LAYER — PRODUCT DATA CACHE
     ------------------------------------------------------------------ */

  // Lightweight product cache — title, url, featured_image for card rendering.
  // Shares the same /products/{handle}.js endpoint fetchBoldData already hits,
  // but stores product metadata separately so Phase 4 can read it without
  // re-fetching or touching the Bold response shape.
  HF.productCache = {};

  HF.fetchProductData = async function (handle) {
    if (HF.productCache[handle]) return HF.productCache[handle];
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
      const data = await res.json();
      HF.productCache[handle] = data;
      return data;
    } catch (err) {
      console.warn('[holster-finder] product fetch failed for ' + handle, err);
      return null;
    }
  };

  HF.fetchBoldData = async function (productHandle) {
    if (HF.boldCache[productHandle]) return HF.boldCache[productHandle];
    try {
      // Resolve product ID from Shopify's product JSON endpoint - Bold needs
      // the numeric ID, not the handle.
      const productJsonRes = await fetch('/products/' + productHandle + '.js');
      if (!productJsonRes.ok) throw new Error('Product JSON fetch failed: ' + productJsonRes.status);
      const productJson = await productJsonRes.json();
      const productId = productJson.id;

      const boldUrl = 'https://options.shopapps.site/v2/' + HF.BOLD_STORE + '/generate_option/' + productId;
      const res = await fetch(boldUrl);
      if (!res.ok) throw new Error('Bold API fetch failed: ' + res.status);
      const data = await res.json();

      // Apply runtime filter: strip inactive option sets before caching.
      // Bold uses option_set_active === false to hide draft/disabled configs.
      if (data.option_product && Array.isArray(data.option_product.option_sets)) {
        data.option_product.option_sets = data.option_product.option_sets.filter(function (s) {
          return s.option_set_active !== false;
        });
      }

      HF.boldCache[productHandle] = data;
      return data;
    } catch (err) {
      console.warn('[holster-finder] Bold fetch failed for ' + productHandle, err);
      return null; // caller drops this product from the candidate set on null
    }
  };

  /* ------------------------------------------------------------------
     DATA LAYER — NORMALIZE
     ------------------------------------------------------------------ */

  // All internal_name comparisons go through normalize(). Bold data has
  // trailing spaces, inconsistent casing, and copy-paste errors - see
  // dictionary anomalies_and_data_quality for the full list.
  HF.normalize = function (s) {
    return (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  };

  /* ------------------------------------------------------------------
     DATA LAYER — OPTION LOOKUPS
     ------------------------------------------------------------------ */

  HF.findOptionByInternalName = function (boldData, internalName) {
    if (!boldData || !boldData.option_product || !boldData.option_product.option_sets || !internalName) return null;
    const target = HF.normalize(internalName);
    for (const set of boldData.option_product.option_sets) {
      for (const opt of (set.options || [])) {
        if (HF.normalize(opt.internal_name) === target) return opt;
      }
    }
    return null;
  };

  // Looks up a Bold option object via the canonical key in the dictionary.
  // For Pattern 1 products (Elites family) the gun option is hand-split
  // (gun.rh / gun.lh) - pass hand ('Right' or 'Left') to disambiguate.
  HF.findOptionByCanonicalKey = function (boldData, productHandle, canonicalKey, hand) {
    const dict = HF.dictionary && HF.dictionary.products[productHandle];
    if (!dict) return null;
    const optionDef = dict.options[canonicalKey];
    if (!optionDef) return null;

    let internalName;
    if (optionDef.rh || optionDef.lh) {
      // Pattern 1: hand-split gun option
      if (!hand) return null;
      const side = hand === 'Left' ? optionDef.lh : optionDef.rh;
      if (!side) return null;
      internalName = side.internal_name;
    } else {
      internalName = optionDef.internal_name;
    }

    const found = HF.findOptionByInternalName(boldData, internalName);
    if (!found) {
      console.warn('[holster-finder] Option not found for canonical key "' + canonicalKey + '" on ' + productHandle + ' — skipping pre-fill for this field');
    }
    return found;
  };

  /* ------------------------------------------------------------------
     DATA LAYER — GUN-TO-LIGHT MAP
     ------------------------------------------------------------------ */

  // Builds a { gunName: [lightValue, ...] } map for per-gun-conditional
  // light products (Types A, B-Elites, D, Optio V2). Each product has its
  // own map — never share across products.
  // Returns null for Type C (MSP family) and C_OWB — they use a universal
  // light list, not per-gun conditions.
  HF.buildGunToLightMap = function (boldData, productHandle, hand) {
    if (!boldData || !boldData.option_product || !boldData.option_product.option_sets) return null;
    const dict = HF.dictionary && HF.dictionary.products[productHandle];
    if (!dict) return null;
    if (!dict.options.light || dict.options.light.pattern !== 'per-gun-conditional') return null;

    // Resolve the correct gun option (hand-aware for Pattern 1 Elites).
    let gunOption;
    if (dict.options.gun && (dict.options.gun.rh || dict.options.gun.lh)) {
      if (!hand) return null;
      const gunDef = hand === 'Left' ? dict.options.gun.lh : dict.options.gun.rh;
      gunOption = HF.findOptionByInternalName(boldData, gunDef.internal_name);
    } else if (dict.options.gun) {
      gunOption = HF.findOptionByInternalName(boldData, dict.options.gun.internal_name);
    }

    if (!gunOption) {
      console.warn('[holster-finder] gun option not found for ' + productHandle + ' — cannot build gun-to-light map');
      return null;
    }

    const gunOptionId = gunOption.id;

    // Walk all option sets, find dropdowns whose internal_name contains 'light'.
    // The type === 'dropdown' filter excludes display-text options that might
    // contain 'light' as a substring (e.g. a "Lighting note" display field).
    const allOptions = boldData.option_product.option_sets.flatMap(function (s) {
      return s.options || [];
    });
    const lightOptions = allOptions.filter(function (o) {
      return o.type === 'dropdown' && HF.normalize(o.internal_name).includes('light');
    });

    const map = {};
    for (const lightOpt of lightOptions) {
      for (const condition of (lightOpt.conditions || [])) {
        for (const rule of (condition.rules || [])) {
          // Confirm this condition rule references the gun option, not some
          // other option (e.g. muzzle device conditions also use this structure).
          if (!rule.option_key || !rule.option_key.includes('_' + gunOptionId + '_')) continue;
          if (rule.operator !== 'equal') continue; // only 'equal' observed; skip unknown operators
          if (!map[rule.value]) map[rule.value] = [];
          map[rule.value] = map[rule.value].concat(lightOpt.options_values || []);
        }
      }
    }

    return map;
  };

  /* ------------------------------------------------------------------
     ELEMENTS
     ------------------------------------------------------------------ */
  HF.els = {
    dialog: null,
    body: null,
    progress: null,
    backBtn: null,
    announce: null,
  };

  /* ------------------------------------------------------------------
     INIT
     ------------------------------------------------------------------ */
  HF.init = function () {
    HF.els.dialog = document.querySelector('dialog.hf-dialog');
    if (!HF.els.dialog) return;

    HF.els.body     = HF.els.dialog.querySelector('.hf-body');
    HF.els.progress = HF.els.dialog.querySelector('.hf-progress');
    HF.els.backBtn  = HF.els.dialog.querySelector('.hf-back-btn');
    HF.els.announce = HF.els.dialog.querySelector('.hf-announce');

    // Bind all open-trigger elements (including the one inside the section)
    document.querySelectorAll('[data-open-holster-finder]').forEach(function (el) {
      el.addEventListener('click', HF.open);
    });

    // Bind close button
    const closeBtn = HF.els.dialog.querySelector('.hf-close-btn');
    if (closeBtn) closeBtn.addEventListener('click', HF.close);

    // Close on backdrop click (click lands on the <dialog> element itself)
    HF.els.dialog.addEventListener('click', function (e) {
      if (e.target === HF.els.dialog || e.target.classList.contains('hf-modal')) {
        HF.close();
      }
    });

    // Esc is handled natively by <dialog> - but we reset state on close
    HF.els.dialog.addEventListener('close', HF.onClose);

    // Back button
    if (HF.els.backBtn) {
      HF.els.backBtn.addEventListener('click', HF.goBack);
    }

    // Auto-open: URL param (?holster-finder=1) only
    const params = new URLSearchParams(window.location.search);
    if (params.get('holster-finder') === '1') {
      HF.open();
    }
  };

  /* ------------------------------------------------------------------
     OPEN / CLOSE
     ------------------------------------------------------------------ */
  HF.open = function () {
    HF.resetState();
    HF.renderQ1();
    HF.els.dialog.showModal();
  };

  HF.close = function () {
    HF.els.dialog.close();
  };

  HF.onClose = function () {
    HF.resetState();
  };

  /* ------------------------------------------------------------------
     STATE HELPERS
     ------------------------------------------------------------------ */
  HF.resetState = function () {
    HF.state.branch     = null;
    HF.state.answers    = {};
    HF.state.history    = [];
    HF.state.candidates = [];
  };

  /* ------------------------------------------------------------------
     PROGRESS DOTS
     ------------------------------------------------------------------ */
  HF.renderProgress = function (currentStep, totalSteps) {
    if (!HF.els.progress) return;
    HF.els.progress.innerHTML = '';
    for (let i = 0; i < totalSteps; i++) {
      const dot = document.createElement('span');
      dot.className = 'hf-dot';
      dot.setAttribute('role', 'presentation');
      if (i < currentStep) {
        dot.classList.add('hf-dot--done');
      } else if (i === currentStep) {
        dot.setAttribute('aria-current', 'step');
      }
      HF.els.progress.appendChild(dot);
    }
  };

  /* ------------------------------------------------------------------
     BACK BUTTON
     ------------------------------------------------------------------ */
  HF.updateBackBtn = function () {
    if (!HF.els.backBtn) return;
    if (HF.state.history.length > 0) {
      HF.els.backBtn.removeAttribute('hidden');
    } else {
      HF.els.backBtn.setAttribute('hidden', '');
    }
  };

  HF.goBack = function () {
    var prev = HF.state.history.pop();
    if (!prev) return;
    // Restore answers + candidates to the snapshot from before that question was answered
    HF.state.answers    = prev.answers;
    HF.state.candidates = prev.candidates;
    HF.renderQuestion(prev.questionId);
    HF.updateBackBtn();
  };

  /* ------------------------------------------------------------------
     ANNOUNCE (ARIA LIVE)
     ------------------------------------------------------------------ */
  HF.announce = function (text) {
    if (!HF.els.announce) return;
    HF.els.announce.textContent = '';
    requestAnimationFrame(function () {
      HF.els.announce.textContent = text;
    });
  };

  /* ------------------------------------------------------------------
     QUESTION RENDERER DISPATCH
     ------------------------------------------------------------------ */
  HF.renderQuestion = function (questionId) {
    var renderers = {
      q1:              HF.renderQ1,
      q2_edc_mag:      HF.renderQ2_edc_mag,
      q2_edc:          HF.renderQ2_edc,
      q2a_owb_light:   HF.renderQ2a_owb_light,
      q2b_light:       HF.renderQ2b_light,
      q2_owb:          HF.renderQ2_owb,
      q2_duty:         HF.renderQ2_duty,
      q2_magcarry:     HF.renderQ2_magcarry,
      q2_quickship:    HF.renderQ2_quickship,
      q3_hand:         HF.renderQ3_hand,
      q3_magcarry:     HF.renderQ3_magcarry,
      q3_quickship:    HF.renderQ3_quickship,
      q4_gun:          HF.renderQ4_gun,
      q4a_duty:        HF.renderQ4a_duty_brand,
      q4b_duty:        HF.renderQ4b_duty_model,
      q4_magcarry:     HF.renderQ4_magcarry_hand,
      q5_edc_light:    HF.renderQ5_edc_light,
      q5b_edc_light:   HF.renderQ5b_edc_light_model,
    };
    var fn = renderers[questionId];
    if (fn) fn();
  };

  /* ------------------------------------------------------------------
     TRANSITION HELPER
     ------------------------------------------------------------------ */
  HF.transitionBody = function (renderFn) {
    const body = HF.els.body;
    if (!body) { renderFn(); return; }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { renderFn(); return; }

    body.classList.add('hf-body--exiting');
    setTimeout(function () {
      body.classList.remove('hf-body--exiting');
      renderFn();
    }, 200);
  };

  /* ------------------------------------------------------------------
     Q1 — CARRY STYLE (hardcoded for Phase 1 skeleton)
     ------------------------------------------------------------------ */
  HF.Q1_CHOICES = [
    {
      id: 'edc',
      icon: hfIcon('<path d="M12 3L4 7.5v4.5c0 4.5 3.4 8.7 8 9.8 4.6-1.1 8-5.3 8-9.8V7.5L12 3z"/>'),
      name: 'Daily concealed carry',
      sub: 'Inside the waistband (IWB)',
    },
    {
      id: 'owb',
      icon: hfIcon('<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>'),
      name: 'Outside the waistband',
      sub: 'Open carry or OWB holster',
    },
    {
      id: 'duty',
      icon: hfIcon('<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>'),
      name: 'Range, duty, or competition',
      sub: 'Full-size OWB platform',
    },
    {
      id: 'magcarry',
      icon: hfIcon('<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>'),
      name: 'I just need a magazine carrier',
      sub: 'Standalone mag carrier',
    },
    {
      id: 'quickship',
      icon: hfIcon('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>'),
      name: 'Quick Ship',
      sub: 'Ships within 24 hours',
    },
  ];

  HF.renderQ1 = function () {
    HF.transitionBody(function () {
      HF.renderProgress(0, 3); // placeholder total until branch is known
      HF.updateBackBtn();
      HF.announce('Step 1: What kind of carry are you setting up?');

      const html = [
        '<p class="hf-question-label">Step 1 of ?</p>',
        '<h2 class="hf-question-text">What kind of carry are you setting up?</h2>',
        '<ul class="hf-choices" role="radiogroup" aria-label="Carry style">',
      ];

      HF.Q1_CHOICES.forEach(function (choice) {
        html.push(
          '<li class="hf-choice" role="none">',
          '  <button class="hf-choice-btn"',
          '          role="radio"',
          '          aria-checked="false"',
          '          data-q1-choice="' + choice.id + '"',
          '  >',
          '    <span class="hf-choice-icon" aria-hidden="true">' + choice.icon + '</span>',
          '    <span class="hf-choice-content">',
          '      <span class="hf-choice-name">' + choice.name + '</span>',
          '      <span class="hf-choice-sub">' + choice.sub + '</span>',
          '    </span>',
          '  </button>',
          '</li>'
        );
      });

      html.push('</ul>');

      HF.els.body.innerHTML = html.join('\n');

      // Q1 handler — sets branch + initial candidate pool, routes to Q2
      HF.els.body.querySelectorAll('[data-q1-choice]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var chosen = btn.getAttribute('data-q1-choice');
          HF.state.branch     = chosen;
          HF.state.candidates = (HF.BRANCH_CANDIDATES[chosen] || []).slice();
          HF.state.answers    = {};
          HF.advance('q1');

          var nextQ = {
            edc:       HF.renderQ2_edc_mag,
            owb:       HF.renderQ2_owb,
            duty:      HF.renderQ2_duty,
            magcarry:  HF.renderQ2_magcarry,
            quickship: HF.renderQ2_quickship,
          };
          (nextQ[chosen] || HF.renderTerminal)();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     PHASE 3 — BRANCH CANDIDATE POOLS + TYPE MAP
     ------------------------------------------------------------------ */

  HF.BRANCH_CANDIDATES = {
    edc:       ['axis-elite', 'xiphos-elite', 'apx', 'agis-elite', 't1m', 'msp-pro-series-flx', 'msp-elite', 'msp', 'spara', 't1-echo'],
    owb:       ['msp-owb', 'spara', 't1-echo'], // Spara + T1-Echo are pancake OWB — crossover with EDC branch
    duty:      ['centurion-v2', 'optio-v2'],
    magcarry:  ['msp-pro-series-flx-mag-only', 'msp-pro-series-mag-only', 'single-magazine-carrier'],
    quickship: ['t1c-quick-ship-holster', 'msp-quick-ship', 'msp-quick-ship-owb', 't1c-quick-ship-xiphos-elite'],
  };

  // Inline type map so branch filtering never needs an async dictionary load.
  // Mirrors dictionary.products[handle].type.
  HF.TYPE = {
    'axis-elite':                  'A',
    'xiphos-elite':                'B',
    'apx':                         'B',
    'agis-elite':                  'B',
    't1m':                         'B_simple',
    'msp-pro-series-flx':          'C',
    'msp-elite':                   'C',
    'msp':                         'C',
    'msp-owb':                     'C_OWB',
    'spara':                       'D',
    't1-echo':                     'D',
    'centurion-v2':                'E',
    'optio-v2':                    'E',
    'msp-pro-series-flx-mag-only': 'F',
    'msp-pro-series-mag-only':     'F',
    'single-magazine-carrier':     'F',
    't1c-quick-ship-holster':      'G',
    'msp-quick-ship':              'G',
    'msp-quick-ship-owb':          'G',
    't1c-quick-ship-xiphos-elite': 'G',
  };

  /* ------------------------------------------------------------------
     PHASE 3 — NAVIGATION HELPERS
     ------------------------------------------------------------------ */

  // Snapshot state before each question so back restores it exactly.
  HF.advance = function (fromQuestionId) {
    HF.state.history.push({
      questionId: fromQuestionId,
      answers:    Object.assign({}, HF.state.answers),
      candidates: HF.state.candidates.slice(),
    });
    HF.updateBackBtn();
  };

  // Bold options_values can be strings or {label, value} objects.
  HF.extractOptionValues = function (options_values) {
    return (options_values || []).map(function (v) {
      if (typeof v === 'string') return v;
      return v.label || v.value || String(v);
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_EDC — Q2 SPARE MAGAZINE
     ------------------------------------------------------------------ */

  HF.renderQ2_edc_mag = function () {
    HF.transitionBody(function () {
      HF.renderProgress(1, 5);
      HF.updateBackBtn();
      HF.announce('Step 2 of 5: Do you want to carry a spare magazine?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 2 of 5</p>',
        '<h2 class="hf-question-text">Do You Want To Carry A Spare Magazine?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Spare magazine">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-mag="yes">',
        '      <span class="hf-choice-icon">' + hfIcon('<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Yes, Attached To The Holster</span>',
        '        <span class="hf-choice-sub">Integrated mag carrier setup</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-mag="no">',
        '      <span class="hf-choice-icon">' + hfIcon('<path d="M12 3L4 7.5v4.5c0 4.5 3.4 8.7 8 9.8 4.6-1.1 8-5.3 8-9.8V7.5L12 3z"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">No, Gun Only</span>',
        '        <span class="hf-choice-sub">Holster only</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-mag]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var mag = btn.getAttribute('data-mag');
          HF.state.answers.spare_mag = mag;

          if (mag === 'yes') {
            // Holsters with a spare mag Bold option:
            // - Axis Elite + Agis Elite: "Extended Spare Mag" = Yes/No (optional add-on)
            // - MSP Pro FLX + MSP Elite: mag carrier is a required configuration axis
            HF.state.candidates = ['axis-elite', 'agis-elite', 'msp-pro-series-flx', 'msp-elite'];
          } else {
            // Gun-only: holsters where a spare mag is either optional (can choose No)
            // or not available at all. MSP Pro FLX and MSP Elite excluded — their mag
            // carrier has no None option, so they can't be ordered gun-only.
            HF.state.candidates = ['axis-elite', 'agis-elite', 'xiphos-elite', 'apx', 't1m', 'msp', 'spara', 't1-echo'];
          }

          HF.advance('q2_edc_mag');
          HF.renderQ3_hand();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_EDC — Q2 LIGHT (kept for OWB branch reuse via renderQ2a_owb_light)
     ------------------------------------------------------------------ */

  HF.renderQ2_edc = function () {
    HF.transitionBody(function () {
      HF.renderProgress(1, 4);
      HF.announce('Step 2 of 4: Are you running a weapon light?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 2 of 4</p>',
        '<h2 class="hf-question-text">Are you running a weapon light?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Weapon light">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-light="yes">',
        '      <span class="hf-choice-icon">' + hfIcon('<circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Yes</span>',
        '        <span class="hf-choice-sub">I have or plan to use a weapon light</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-light="no">',
        '      <span class="hf-choice-icon">' + hfIcon('<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">No</span>',
        '        <span class="hf-choice-sub">Just the holster, no light</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-light="notsure">',
        '      <span class="hf-choice-icon">' + hfIcon('<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Not sure yet</span>',
        '        <span class="hf-choice-sub">Show me both options</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-light]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var light = btn.getAttribute('data-light');
          HF.state.answers.light = light;

          // Type A = Axis Elite (light-bearing IWB)
          // Type B / B_simple = Xiphos, APX, Agis, T1-M (no-light elites)
          // Type C = MSP family (light-keyed — only surfaces on explicit Yes)
          // Type D = Spara, T1-Echo (can run with or without light)
          HF.state.candidates = HF.state.candidates.filter(function (h) {
            var t = HF.TYPE[h];
            if (light === 'yes')     return t === 'A' || t === 'C' || t === 'D';
            if (light === 'no')      return t === 'B' || t === 'B_simple' || t === 'D';
            /* notsure */            return t === 'A' || t === 'B' || t === 'B_simple' || t === 'D';
          });

          HF.advance('q2_edc');
          if (light === 'yes') {
            HF.renderQ2b_light();
          } else {
            HF.renderQ3_hand();
          }
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     SHARED Q3 — HANDEDNESS (edc, owb, duty)
     ------------------------------------------------------------------ */

  HF.renderQ3_hand = function () {
    HF.transitionBody(function () {
      var stepNum = HF.state.history.length + 1;
      var remainingAfter = (HF.state.branch === 'duty') ? 2 : 1; // duty has 2 more (brand + model)
      var total   = stepNum + remainingAfter;

      HF.renderProgress(stepNum, total);
      HF.announce('Step ' + (stepNum + 1) + ' of ' + total + ': Right or left handed?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step ' + (stepNum + 1) + ' of ' + total + '</p>',
        '<h2 class="hf-question-text">Right or left handed?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Dominant hand">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-hand="Right">',
        '      <span class="hf-choice-icon">' + hfIcon('<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Right handed</span>',
        '        <span class="hf-choice-sub">Most common</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-hand="Left">',
        '      <span class="hf-choice-icon">' + hfIcon('<line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Left handed</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-hand]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          HF.state.answers.hand = btn.getAttribute('data-hand');
          HF.advance('q3_hand');
          if (HF.state.branch === 'duty') {
            HF.renderQ4a_duty_brand();
          } else {
            HF.renderQ4_gun();
          }
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     SHARED Q4 — GUN SEARCH (edc + owb)
     ------------------------------------------------------------------ */

  HF.renderQ4_gun = async function () {
    // If all remaining candidates are MSP family (light-keyed, no gun option),
    // skip the gun question — the gun list would be empty and MSP is configured by light, not gun.
    var allMsp = HF.state.candidates.every(function (h) {
      var t = HF.TYPE[h]; return t === 'C' || t === 'C_OWB';
    });
    if (allMsp) {
      HF.advance('q4_gun');
      if (HF.state.branch === 'edc') {
        HF.renderQ5_edc_light();
      } else {
        HF.renderTerminal();
      }
      return;
    }

    HF.els.body.innerHTML = '<div class="hf-loading"><p>Loading gun options...</p></div>';

    var hand  = HF.state.answers.hand;
    var isOwb = HF.state.branch === 'owb';
    var dict  = await HF.loadDictionary();
    if (!dict) return;

    // Fetch Bold data for all candidates that have a gun option; skip MSP family
    var gunHandles = HF.state.candidates.filter(function (h) {
      var t = HF.TYPE[h]; return t !== 'C' && t !== 'C_OWB';
    });

    var results = await Promise.all(
      gunHandles.map(function (h) {
        return HF.fetchBoldData(h).then(function (data) { return { handle: h, data: data }; });
      })
    );

    // gun name → [product handles that support it]
    var gunToCandidates = {};
    var allGuns = [];

    results.forEach(function (r) {
      if (!r.data) return;
      var gunOption = HF.findOptionByCanonicalKey(r.data, r.handle, 'gun', hand);
      if (!gunOption) return;
      HF.extractOptionValues(gunOption.options_values).forEach(function (gun) {
        if (!gunToCandidates[gun]) { gunToCandidates[gun] = []; allGuns.push(gun); }
        if (gunToCandidates[gun].indexOf(r.handle) === -1) gunToCandidates[gun].push(r.handle);
      });
    });

    allGuns.sort();

    HF.transitionBody(function () {
      var stepNum = HF.state.history.length + 1;
      var total   = stepNum; // gun is the last question in edc/owb path
      HF.renderProgress(stepNum - 1, total);
      HF.announce('Step ' + stepNum + ' of ' + total + ': What\'s your handgun?');

      var rows = allGuns.map(function (gun) {
        var safe = gun.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        return '<li role="option" class="hf-gun-option" tabindex="0" data-gun="' + safe + '">' + gun.replace(/&/g, '&amp;') + '</li>';
      });

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step ' + stepNum + ' of ' + total + '</p>',
        '<h2 class="hf-question-text">What\'s your handgun?</h2>',
        '<input class="hf-gun-search" type="search" placeholder="Type to filter..." aria-label="Search handguns" autocomplete="off">',
        '<ul class="hf-gun-list" role="listbox" aria-label="Handgun options">',
        rows.join(''),
        isOwb ? '<li role="option" class="hf-gun-option hf-gun-option--universal" tabindex="0" data-gun="__other__">My gun isn\'t listed — MSP OWB fits all</li>' : '',
        '</ul>',
      ].join('\n');

      var searchEl = HF.els.body.querySelector('.hf-gun-search');
      var listEl   = HF.els.body.querySelector('.hf-gun-list');

      if (searchEl) {
        searchEl.addEventListener('input', function () {
          var q = HF.normalize(searchEl.value);
          listEl.querySelectorAll('.hf-gun-option').forEach(function (li) {
            li.style.display = (!q || HF.normalize(li.getAttribute('data-gun') || '').includes(q)) ? '' : 'none';
          });
        });
        setTimeout(function () { searchEl.focus(); }, 50);
      }

      function selectGun(li) {
        var gun = li.getAttribute('data-gun');
        HF.state.answers.gun = (gun === '__other__') ? null : gun;

        if (gun !== '__other__') {
          // MSP family (C/C_OWB) always kept — gun-filtering only trims non-MSP candidates.
          // OWB also filters now that Spara + T1-Echo are in the pool.
          var supporting = gunToCandidates[gun] || [];
          HF.state.candidates = HF.state.candidates.filter(function (h) {
            var t = HF.TYPE[h];
            if (t === 'C' || t === 'C_OWB') return true;
            return supporting.indexOf(h) !== -1;
          });
        }

        HF.advance('q4_gun');
        if (HF.state.branch === 'edc') {
          HF.renderQ5_edc_light();
        } else {
          HF.renderTerminal();
        }
      }

      listEl.querySelectorAll('.hf-gun-option').forEach(function (li) {
        li.addEventListener('click', function () { selectGun(li); });
        li.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectGun(li); }
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_EDC — Q5 WEAPON LIGHT Y/N (post-gun, EDC only)
     ------------------------------------------------------------------ */

  HF.renderQ5_edc_light = function () {
    HF.transitionBody(function () {
      var stepNum = HF.state.history.length + 1;
      var total   = stepNum; // light is the last question
      HF.renderProgress(stepNum - 1, total);
      HF.announce('Step ' + stepNum + ' of ' + total + ': Does your gun have a weapon light attached?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step ' + stepNum + ' of ' + total + '</p>',
        '<h2 class="hf-question-text">Does Your Gun Have A Weapon Light Attached?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Weapon light">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-q5-light="yes">',
        '      <span class="hf-choice-icon">' + hfIcon('<circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Yes</span>',
        '        <span class="hf-choice-sub">I have or plan to use a weapon light</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-q5-light="no">',
        '      <span class="hf-choice-icon">' + hfIcon('<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">No</span>',
        '        <span class="hf-choice-sub">Just the holster, no light</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-q5-light]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var light = btn.getAttribute('data-q5-light');
          HF.state.answers.light = light;

          HF.state.candidates = HF.state.candidates.filter(function (h) {
            var t = HF.TYPE[h];
            // yes: all holsters with a light Bold option (A, B, C, D) — B_simple (T1-M) has no light option
            // no: all holsters where light is optional (A, B, B_simple, D) — C (MSP family) requires a light selection
            if (light === 'yes') return t === 'A' || t === 'B' || t === 'C' || t === 'D';
            /* no */             return t === 'A' || t === 'B' || t === 'B_simple' || t === 'D';
          });

          HF.advance('q5_edc_light');
          if (light === 'yes') {
            HF.renderQ5b_edc_light_model();
          } else {
            HF.renderTerminal();
          }
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_EDC — Q5b WHICH WEAPON LIGHT (post-gun, EDC only)
     ------------------------------------------------------------------ */

  HF.renderQ5b_edc_light_model = function () {
    HF.transitionBody(function () {
      var stepNum = HF.state.history.length + 1;
      var total   = stepNum;
      HF.renderProgress(stepNum - 1, total);
      HF.announce('Step ' + stepNum + ' of ' + total + ': Which weapon light?');

      var lights = [
        { id: 'tlr1',    label: 'Streamlight TLR-1 / TLR-1 HL', sub: 'Full-size, most common' },
        { id: 'tlr7',    label: 'Streamlight TLR-7 / TLR-7A',   sub: 'Compact, flush-fit' },
        { id: 'tlr7sub', label: 'Streamlight TLR-7 Sub',        sub: 'Sub-compact pistols' },
        { id: 'x300',    label: 'Surefire X300U',               sub: 'X300U-A or X300U-B' },
        { id: 'plpro',   label: 'Olight PL-Pro / Baldr Pro',    sub: 'Olight series' },
        { id: 'tcm',     label: 'Nightstick TCM',               sub: 'Compact series' },
        { id: 'other',   label: 'Other / not sure yet',         sub: 'Show me all compatible' },
      ];

      var items = lights.map(function (l) {
        return [
          '<li class="hf-choice" role="none">',
          '  <button class="hf-choice-btn" role="radio" aria-checked="false" data-light-model="' + l.id + '">',
          '    <span class="hf-choice-content">',
          '      <span class="hf-choice-name">' + l.label + '</span>',
          l.sub ? '      <span class="hf-choice-sub">' + l.sub + '</span>' : '',
          '    </span>',
          '  </button>',
          '</li>',
        ].join('\n');
      });

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step ' + stepNum + ' of ' + total + '</p>',
        '<h2 class="hf-question-text">Which Weapon Light?</h2>',
        '<ul class="hf-choices" role="radiogroup" aria-label="Weapon light model">',
        items.join(''),
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-light-model]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          HF.state.answers.light_model = btn.getAttribute('data-light-model');
          HF.advance('q5b_edc_light');
          HF.renderTerminal();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_EDC/OWB — Q2a OWB LIGHT Y/N
     ------------------------------------------------------------------ */

  HF.renderQ2a_owb_light = function () {
    HF.transitionBody(function () {
      var stepNum = HF.state.history.length + 1;
      var total   = stepNum + 2; // Q3_hand + Q4_gun remain
      HF.renderProgress(stepNum - 1, total);
      HF.announce('Step ' + stepNum + ' of ' + total + ': Are you running a weapon light?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step ' + stepNum + ' of ' + total + '</p>',
        '<h2 class="hf-question-text">Are you running a weapon light?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Weapon light">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-owb-light="yes">',
        '      <span class="hf-choice-icon">' + hfIcon('<circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Yes</span>',
        '        <span class="hf-choice-sub">I have or plan to use a weapon light</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-owb-light="no">',
        '      <span class="hf-choice-icon">' + hfIcon('<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">No</span>',
        '        <span class="hf-choice-sub">Just the holster, no light</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-owb-light]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var light = btn.getAttribute('data-owb-light');
          HF.state.answers.light = light;
          HF.advance('q2a_owb_light');
          if (light === 'yes') {
            HF.renderQ2b_light();
          } else {
            HF.renderQ3_hand();
          }
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_EDC/OWB — Q2b WHICH WEAPON LIGHT
     ------------------------------------------------------------------ */

  HF.renderQ2b_light = function () {
    HF.transitionBody(function () {
      var stepNum = HF.state.history.length + 1;
      var total   = stepNum + 2; // Q3_hand + Q4_gun remain
      HF.renderProgress(stepNum - 1, total);
      HF.announce('Step ' + stepNum + ' of ' + total + ': Which weapon light?');

      var lights = [
        { id: 'tlr1',    label: 'Streamlight TLR-1 / TLR-1 HL', sub: 'Full-size, most common' },
        { id: 'tlr7',    label: 'Streamlight TLR-7 / TLR-7A',   sub: 'Compact, flush-fit' },
        { id: 'tlr7sub', label: 'Streamlight TLR-7 Sub',        sub: 'Sub-compact pistols' },
        { id: 'x300',    label: 'Surefire X300U',               sub: 'X300U-A or X300U-B' },
        { id: 'plpro',   label: 'Olight PL-Pro / Baldr Pro',    sub: 'Olight series' },
        { id: 'tcm',     label: 'Nightstick TCM',               sub: 'Compact series' },
        { id: 'other',   label: 'Other / not sure yet',         sub: 'Show me all compatible' },
      ];

      var items = lights.map(function (l) {
        return [
          '<li class="hf-choice" role="none">',
          '  <button class="hf-choice-btn" role="radio" aria-checked="false" data-light-model="' + l.id + '">',
          '    <span class="hf-choice-content">',
          '      <span class="hf-choice-name">' + l.label + '</span>',
          l.sub ? '      <span class="hf-choice-sub">' + l.sub + '</span>' : '',
          '    </span>',
          '  </button>',
          '</li>',
        ].join('\n');
      });

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step ' + stepNum + ' of ' + total + '</p>',
        '<h2 class="hf-question-text">Which weapon light?</h2>',
        '<ul class="hf-choices" role="radiogroup" aria-label="Weapon light model">',
        items.join(''),
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-light-model]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          HF.state.answers.light_model = btn.getAttribute('data-light-model');
          HF.advance('q2b_light');
          HF.renderQ3_hand();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_OWB — Q2
     ------------------------------------------------------------------ */

  HF.renderQ2_owb = function () {
    HF.transitionBody(function () {
      HF.renderProgress(1, 3);
      HF.announce('Step 2 of 3: Daily OWB or range/duty?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 2 of 3</p>',
        '<h2 class="hf-question-text">How will you use this holster?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="OWB use">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-owb="daily">',
        '      <span class="hf-choice-icon">' + hfIcon('<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Daily OWB</span>',
        '        <span class="hf-choice-sub">Concealment-friendly, lower profile</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-owb="duty">',
        '      <span class="hf-choice-icon">' + hfIcon('<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>') + '</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Range, duty, or competition</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-owb]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var choice = btn.getAttribute('data-owb');
          HF.advance('q2_owb');
          if (choice === 'duty') {
            // Pivot branch to duty
            HF.state.branch     = 'duty';
            HF.state.candidates = HF.BRANCH_CANDIDATES.duty.slice();
            HF.renderQ2_duty();
          } else {
            HF.state.answers.owb_use = 'daily';
            HF.renderQ2a_owb_light();
          }
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_DUTY — Q2 OPTIC
     ------------------------------------------------------------------ */

  HF.renderQ2_duty = function () {
    HF.transitionBody(function () {
      HF.renderProgress(1, 4);
      HF.announce('Step 2 of 4: Is your handgun running an optic?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 2 of 4</p>',
        '<h2 class="hf-question-text">Is your handgun running an optic?</h2>',
        '<ul class="hf-choices" role="radiogroup" aria-label="Optic">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-optic="yes-cowiron">',
        '      <span class="hf-choice-icon" aria-hidden="true">🔭</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Yes, co-witnessed with irons</span>',
        '        <span class="hf-choice-sub">Rear sight location matters for holster fit</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-optic="no">',
        '      <span class="hf-choice-icon" aria-hidden="true">🎯</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">No optic, iron sights only</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-optic="yes-noiron">',
        '      <span class="hf-choice-icon" aria-hidden="true">🔭</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Optic only, no irons</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-optic]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var optic = btn.getAttribute('data-optic');
          HF.state.answers.optic = optic;
          // Optic users → Optio V2 only; no-optic → both Centurion V2 + Optio V2
          if (optic !== 'no') HF.state.candidates = ['optio-v2'];
          HF.advance('q2_duty');
          HF.renderQ3_hand();
        });
      });
    });
  };

  // Q4a — Centurion V2 brand picker (skipped if only Optio V2 remains)
  HF.renderQ4a_duty_brand = function () {
    if (HF.state.candidates.indexOf('centurion-v2') === -1) {
      HF.renderQ4b_duty_model(); return;
    }

    HF.transitionBody(function () {
      HF.renderProgress(3, 4);
      HF.announce('Step 4 of 4: What brand is your handgun?');

      var brands = ['Glock', 'Sig Sauer', 'ZEV', 'Smith & Wesson', 'Staccato', 'Walther'];
      var items  = brands.map(function (b) {
        return '<li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-brand="' + b + '"><span class="hf-choice-content"><span class="hf-choice-name">' + b + '</span></span></button></li>';
      });

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 4 of 4</p>',
        '<h2 class="hf-question-text">What brand is your handgun?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Gun brand">',
        items.join(''),
        '<li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-brand="__other__"><span class="hf-choice-content"><span class="hf-choice-name">None of these / Other</span><span class="hf-choice-sub">FN, HK, CZ, Springfield, etc.</span></span></button></li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-brand]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var brand = btn.getAttribute('data-brand');
          if (brand === '__other__') {
            // [G] escape — Centurion doesn't cover this brand, drop it
            HF.state.candidates = HF.state.candidates.filter(function (h) { return h !== 'centurion-v2'; });
            HF.state.answers.brand = null;
          } else {
            HF.state.answers.brand = brand;
          }
          HF.advance('q4a_duty');
          HF.renderQ4b_duty_model();
        });
      });
    });
  };

  // Q4b — Gun model (Centurion brand-filtered or Optio V2 full list)
  HF.renderQ4b_duty_model = async function () {
    HF.els.body.innerHTML = '<div class="hf-loading"><p>Loading gun options...</p></div>';

    var dict  = await HF.loadDictionary();
    if (!dict) return;

    var hand  = HF.state.answers.hand;
    var brand = HF.state.answers.brand;

    // Centurion uses brand-specific model keys; map brand name → dictionary key
    var BRAND_KEY = {
      'Glock':          'gun_model_glock',
      'Sig Sauer':      'gun_model_sig',
      'ZEV':            'gun_model_zev',
      'Smith & Wesson': 'gun_model_sw',
      'Staccato':       'gun_model_staccato',
      'Walther':        'gun_model_walther',
    };

    var gunToCandidates = {};
    var allGuns = [];

    var results = await Promise.all(
      HF.state.candidates.map(function (h) {
        return HF.fetchBoldData(h).then(function (data) { return { handle: h, data: data }; });
      })
    );

    results.forEach(function (r) {
      if (!r.data) return;
      var h      = r.handle;
      var key    = (h === 'centurion-v2' && brand) ? BRAND_KEY[brand] : 'gun';
      var option = HF.findOptionByCanonicalKey(r.data, h, key, hand);
      if (!option) return;
      HF.extractOptionValues(option.options_values).forEach(function (gun) {
        if (!gunToCandidates[gun]) { gunToCandidates[gun] = []; allGuns.push(gun); }
        if (gunToCandidates[gun].indexOf(h) === -1) gunToCandidates[gun].push(h);
      });
    });

    allGuns.sort();

    HF.transitionBody(function () {
      var rows = allGuns.map(function (gun) {
        var safe = gun.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        return '<li role="option" class="hf-gun-option" tabindex="0" data-gun="' + safe + '">' + gun.replace(/&/g, '&amp;') + '</li>';
      });

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 4 of 4</p>',
        '<h2 class="hf-question-text">What\'s your handgun model?</h2>',
        '<input class="hf-gun-search" type="search" placeholder="Type to filter..." aria-label="Search gun models" autocomplete="off">',
        '<ul class="hf-gun-list" role="listbox" aria-label="Gun models">',
        rows.join(''),
        '</ul>',
      ].join('\n');

      var searchEl = HF.els.body.querySelector('.hf-gun-search');
      var listEl   = HF.els.body.querySelector('.hf-gun-list');

      if (searchEl) {
        searchEl.addEventListener('input', function () {
          var q = HF.normalize(searchEl.value);
          listEl.querySelectorAll('.hf-gun-option').forEach(function (li) {
            li.style.display = (!q || HF.normalize(li.getAttribute('data-gun') || '').includes(q)) ? '' : 'none';
          });
        });
        setTimeout(function () { searchEl.focus(); }, 50);
      }

      listEl.querySelectorAll('.hf-gun-option').forEach(function (li) {
        li.addEventListener('click', function () {
          var gun = li.getAttribute('data-gun');
          HF.state.answers.model = gun;
          var supporting = gunToCandidates[gun] || [];
          HF.state.candidates = HF.state.candidates.filter(function (h) {
            return supporting.indexOf(h) !== -1;
          });
          HF.advance('q4b_duty');
          HF.renderTerminal();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_MAGCARRY — QUESTIONS
     ------------------------------------------------------------------ */

  HF.renderQ2_magcarry = function () {
    HF.transitionBody(function () {
      HF.renderProgress(1, 3);
      HF.announce('Step 2 of 3: Paired with an MSP holster or standalone?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 2 of 3</p>',
        '<h2 class="hf-question-text">Is this paired with an MSP holster you already have?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Mag carrier type">',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-magtype="paired">',
        '      <span class="hf-choice-icon" aria-hidden="true">🔗</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Paired with my MSP Pro / Elite / FLX</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '  <li class="hf-choice" role="none">',
        '    <button class="hf-choice-btn" role="radio" aria-checked="false" data-magtype="standalone">',
        '      <span class="hf-choice-icon" aria-hidden="true">🎒</span>',
        '      <span class="hf-choice-content">',
        '        <span class="hf-choice-name">Standalone — any pistol</span>',
        '      </span>',
        '    </button>',
        '  </li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-magtype]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var choice = btn.getAttribute('data-magtype');
          HF.state.answers.mag_type = choice;
          HF.advance('q2_magcarry');
          if (choice === 'standalone') {
            HF.state.candidates = ['single-magazine-carrier'];
            HF.renderQ4_magcarry_hand();
          } else {
            HF.state.candidates = ['msp-pro-series-flx-mag-only'];
            HF.renderQ3_magcarry();
          }
        });
      });
    });
  };

  HF.renderQ3_magcarry = function () {
    HF.transitionBody(function () {
      HF.renderProgress(2, 3);
      HF.announce('Step 3 of 3: Magazine stack type?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 3 of 3</p>',
        '<h2 class="hf-question-text">Single, double, or triple stack magazine?</h2>',
        '<ul class="hf-choices" role="radiogroup" aria-label="Magazine stack">',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-stack="single"><span class="hf-choice-content"><span class="hf-choice-name">Single stack</span><span class="hf-choice-sub">Thinner magazines (1911, P365, etc.)</span></span></button></li>',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-stack="double"><span class="hf-choice-content"><span class="hf-choice-name">Double stack</span><span class="hf-choice-sub">Most common (Glock 17/19, P320, etc.)</span></span></button></li>',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-stack="triple"><span class="hf-choice-content"><span class="hf-choice-name">Triple stack</span></span></button></li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-stack]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          HF.state.answers.mag_stack = btn.getAttribute('data-stack');
          HF.advance('q3_magcarry');
          HF.renderQ4_magcarry_hand();
        });
      });
    });
  };

  HF.renderQ4_magcarry_hand = function () {
    HF.transitionBody(function () {
      HF.renderProgress(2, 3);
      HF.announce('Step 3 of 3: Which side?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 3 of 3</p>',
        '<h2 class="hf-question-text">Right or left side?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Carrier side">',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-hand="Right"><span class="hf-choice-content"><span class="hf-choice-name">Right side</span></span></button></li>',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-hand="Left"><span class="hf-choice-content"><span class="hf-choice-name">Left side</span></span></button></li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-hand]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          HF.state.answers.hand = btn.getAttribute('data-hand');
          HF.advance('q4_magcarry');
          HF.renderTerminal();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     BRANCH_QUICKSHIP — QUESTIONS
     ------------------------------------------------------------------ */

  HF.renderQ2_quickship = function () {
    HF.transitionBody(function () {
      HF.renderProgress(1, 3);
      HF.announce('Step 2 of 3: What platform is your handgun?');

      var platforms = [
        { id: 'glock', label: 'Glock' },
        { id: 'sig',   label: 'Sig Sauer' },
        { id: 'sw',    label: 'Smith & Wesson' },
        { id: '1911',  label: '1911' },
        { id: 'other', label: 'Other platform' },
      ];

      var items = platforms.map(function (p) {
        return '<li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-platform="' + p.id + '"><span class="hf-choice-content"><span class="hf-choice-name">' + p.label + '</span></span></button></li>';
      });

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 2 of 3</p>',
        '<h2 class="hf-question-text">What platform is your handgun?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Gun platform">',
        items.join(''),
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-platform]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var platform = btn.getAttribute('data-platform');
          HF.advance('q2_quickship');
          if (platform === 'other') {
            // Quick Ship doesn't cover this gun — pivot to EDC
            HF.state.branch     = 'edc';
            HF.state.candidates = HF.BRANCH_CANDIDATES.edc.slice();
            HF.state.answers    = {};
            HF.renderQ2_edc();
          } else {
            HF.state.answers.platform = platform;
            HF.renderQ3_quickship();
          }
        });
      });
    });
  };

  HF.renderQ3_quickship = function () {
    HF.transitionBody(function () {
      HF.renderProgress(2, 3);
      HF.announce('Step 3 of 3: Are you running a weapon light?');

      HF.els.body.innerHTML = [
        '<p class="hf-question-label">Step 3 of 3</p>',
        '<h2 class="hf-question-text">Are you running a weapon light?</h2>',
        '<ul class="hf-choices hf-choices--2col" role="radiogroup" aria-label="Light">',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-qs-light="yes"><span class="hf-choice-icon">' + hfIcon('<circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/>') + '</span><span class="hf-choice-content"><span class="hf-choice-name">Yes, with a light</span></span></button></li>',
        '  <li class="hf-choice" role="none"><button class="hf-choice-btn" role="radio" aria-checked="false" data-qs-light="no"><span class="hf-choice-icon">' + hfIcon('<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>') + '</span><span class="hf-choice-content"><span class="hf-choice-name">No light</span></span></button></li>',
        '</ul>',
      ].join('\n');

      HF.els.body.querySelectorAll('[data-qs-light]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var light = btn.getAttribute('data-qs-light');
          HF.state.answers.light = light;
          HF.state.candidates = (light === 'yes')
            ? ['t1c-quick-ship-holster', 'msp-quick-ship']
            : ['t1c-quick-ship-xiphos-elite'];
          HF.advance('q3_quickship');
          HF.renderTerminal();
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     PHASE 4 — PRODUCT BULLETS (hand-curated recommendation copy)
     ------------------------------------------------------------------ */

  HF.PRODUCT_BULLETS = {
    'axis-elite':                  ['Our Most Popular EDC Setup', 'Ideal For All-Day Appendix Carry', 'Balances Concealment + Quick Access'],
    'xiphos-elite':                ['Elite IWB For Light-Ready Setups', 'Premium Build Quality', 'Slim Profile For All-Day Comfort'],
    'apx':                         ['Versatile EDC Platform', 'Adjustable Retention And Cant', 'Works For A Wide Range Of Firearms'],
    'agis-elite':                  ['Slim, Lightweight IWB Design', 'Consistent Retention Every Draw', 'Built For Everyday Carry'],
    't1m':                         ['Minimalist Carry Setup', 'Lightweight And Low-Profile', 'Simple, Reliable Everyday Carry'],
    'msp-pro-series-flx':          ['Holster + Mag Carrier In One', 'Most Versatile EDC Platform', 'Fully Configurable For Your Setup'],
    'msp-elite':                   ['Universal Weapon Light Compatibility', 'Swap Configurations In Seconds', 'Built For Light-Ready EDC'],
    'msp':                         ['Simple, Reliable Light-Bearing Setup', 'Universal Weapon Light Compatibility', 'Entry-Level MSP Platform'],
    'msp-owb':                     ['OWB Light-Bearing Platform', 'Ideal For Range And Open Carry', 'Configures To Your Exact Setup'],
    'spara':                       ['Runs IWB Or OWB', 'Versatile All-Day Carry', 'Comfortable For Extended Wear'],
    't1-echo':                     ['IWB Or OWB Adjustable', 'Range-Ready And Carry-Capable', 'Durable, Purpose-Built Platform'],
    'centurion-v2':                ['Full-Size Duty-Grade Retention', 'Designed For Competition And Service', 'Covers Most Major Duty Pistols'],
    'optio-v2':                    ['Optic-Ready Duty Platform', 'Built For Full-Size Pistols', 'Retention Built For Service Use'],
    'msp-pro-series-flx-mag-only': ['Pairs Directly With MSP Pro FLX', 'Adds Mag Carry To Your Current Rig', 'Seamless Integration'],
    'msp-pro-series-mag-only':     ['Pairs With MSP Pro Series', 'Dedicated Mag Carrier Platform', 'Solid, Purpose-Built Design'],
    'single-magazine-carrier':     ['Universal Fit, Any Pistol', 'Standalone Mag Carrier', 'Simple, Reliable Setup'],
    't1c-quick-ship-holster':      ['Ships Within 24-48 Hours', 'Ready To Configure And Go', 'Fast Turnaround, No Compromise'],
    'msp-quick-ship':              ['MSP Platform, Ships Fast', 'Light-Compatible Quick Ship Option', 'Ready In 24 Hours'],
    'msp-quick-ship-owb':          ['OWB Quick Ship Option', 'Ships Within 24 Hours', 'Ideal For Range Or Open Carry'],
    't1c-quick-ship-xiphos-elite': ['Xiphos Elite, Ships Fast', 'Premium Build, Quick Turnaround', 'Ready Within 24 Hours'],
  };

  /* ------------------------------------------------------------------
     PHASE 4 — ANSWER SUMMARY
     ------------------------------------------------------------------ */

  HF.buildAnswerSummary = function () {
    var a      = HF.state.answers;
    var branch = HF.state.branch;

    var branchLabel = {
      edc: 'Daily EDC', owb: 'OWB', duty: 'Range / Duty',
      magcarry: 'Mag Carrier', quickship: 'Quick Ship',
    }[branch] || branch;

    var lightModelLabel = {
      tlr1: 'TLR-1 / TLR-1 HL', tlr7: 'TLR-7 / TLR-7A', tlr7sub: 'TLR-7 Sub',
      x300: 'Surefire X300U', plpro: 'Olight PL-Pro', tcm: 'Nightstick TCM',
    };

    var pairs = [{ k: 'Carry', v: branchLabel }];
    if (a.spare_mag) {
      pairs.push({ k: 'Spare Mag', v: a.spare_mag === 'yes' ? 'Yes' : 'No' });
    }
    if (a.light) {
      pairs.push({ k: 'Light', v: a.light === 'yes' ? 'Yes' : 'No' });
    }
    if (a.light_model && a.light_model !== 'other') {
      pairs.push({ k: 'Light model', v: lightModelLabel[a.light_model] || a.light_model });
    }
    if (a.hand)  pairs.push({ k: 'Hand',    v: a.hand });
    if (a.gun)   pairs.push({ k: 'Firearm', v: a.gun });
    if (a.brand) pairs.push({ k: 'Brand',   v: a.brand });
    if (a.model) pairs.push({ k: 'Model',   v: a.model });

    var items = pairs.map(function (p) {
      return '<span class="hf-summary-item"><span class="hf-summary-key">' + p.k + '</span><span class="hf-summary-val">' + p.v + '</span></span>';
    });

    // Interleave dot separators
    var parts = [];
    items.forEach(function (item, i) {
      parts.push(item);
      if (i < items.length - 1) parts.push('<span class="hf-summary-sep" aria-hidden="true">·</span>');
    });

    return '<div class="hf-summary" aria-label="Your selections">' + parts.join('') + '</div>';
  };

  /* ------------------------------------------------------------------
     PHASE 4 — HERO PICK
     ------------------------------------------------------------------ */

  // Returns the handle of the best candidate based on branch + light answer.
  // Priority tiers favour the holster type most suited to the user's stated needs.
  HF.pickHero = function () {
    var candidates = HF.state.candidates;
    if (!candidates.length) return null;
    var branch = HF.state.branch;
    var light  = HF.state.answers.light;

    var tiers;
    if (branch === 'edc') {
      if      (light === 'yes')  tiers = ['A', 'C', 'D', 'B', 'B_simple'];
      else if (light === 'no')   tiers = ['B', 'B_simple', 'D', 'A', 'C'];
      else                       tiers = ['A', 'B', 'D', 'B_simple', 'C'];
    } else if (branch === 'owb') {
      tiers = (light === 'yes') ? ['C_OWB', 'D'] : ['D', 'C_OWB'];
    } else {
      return candidates[0]; // duty/magcarry/quickship: first surviving candidate
    }

    for (var i = 0; i < tiers.length; i++) {
      var tier = tiers[i];
      var found = candidates.find(function (h) { return HF.TYPE[h] === tier; });
      if (found) return found;
    }
    return candidates[0];
  };

  /* ------------------------------------------------------------------
     PHASE 4 — URL PARAM BUILDER
     ------------------------------------------------------------------ */

  HF.buildPdpUrl = function (handle, productData) {
    var a  = HF.state.answers;
    var qs = new URLSearchParams();
    if (a.hand        && a.hand        !== '__other__') qs.set('hand',  a.hand);
    if (a.gun         && a.gun         !== '__other__') qs.set('gun',   a.gun);
    if (a.brand)                                        qs.set('brand', a.brand);
    if (a.model)                                        qs.set('model', a.model);
    if (a.light_model && a.light_model !== 'other')     qs.set('light', a.light_model);

    var base = (productData && productData.url) ? productData.url : '/products/' + handle;
    var query = qs.toString();
    return base + (query ? '?' + query : '');
  };

  /* ------------------------------------------------------------------
     PHASE 4 — IMAGE HELPER
     ------------------------------------------------------------------ */

  // Shopify CDN: insert _{size} before the file extension so the CDN returns
  // a resized image. e.g. image.jpg → image_600x.jpg
  HF.shopifyImgSrc = function (src, size) {
    if (!src) return null;
    return src.replace(/(\.(jpg|jpeg|png|gif|webp))(\?|$)/i, '_' + size + '$1$3');
  };

  /* ------------------------------------------------------------------
     PHASE 4 — CARD BUILDERS
     ------------------------------------------------------------------ */

  HF.buildHeroCardHtml = function (handle, productData) {
    var title = productData ? productData.title : handle;
    var raw   = productData ? (typeof productData.featured_image === 'string'
      ? productData.featured_image
      : (productData.featured_image && productData.featured_image.src) || null) : null;
    var img   = HF.shopifyImgSrc(raw, '600x');
    var url   = HF.buildPdpUrl(handle, productData);
    var safe  = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

    var bullets = HF.PRODUCT_BULLETS[handle] || [];
    var bulletHtml = bullets.length
      ? '<ul class="hf-hero-bullets">' + bullets.map(function (b) { return '<li>' + b + '</li>'; }).join('') + '</ul>'
      : '';

    return [
      '<div class="hf-hero-card">',
      img
        ? '  <div class="hf-hero-img-wrap"><img class="hf-hero-img" src="' + img + '" alt="' + title.replace(/"/g, '&quot;') + '" loading="lazy"></div>'
        : '  <div class="hf-hero-img-wrap hf-hero-img-wrap--empty"></div>',
      '  <div class="hf-hero-body">',
      '    <p class="hf-hero-pick-label">Recommended For You</p>',
      '    <h3 class="hf-hero-title">' + title + '</h3>',
      bulletHtml,
      '    <button class="hf-hero-cta" data-pdp-link="' + safe + '">Build Your Holster &#8594;</button>',
      '  </div>',
      '</div>',
    ].join('\n');
  };

  HF.buildAltCardHtml = function (handle, productData) {
    var title = productData ? productData.title : handle;
    var raw   = productData ? (typeof productData.featured_image === 'string'
      ? productData.featured_image
      : (productData.featured_image && productData.featured_image.src) || null) : null;
    var img   = HF.shopifyImgSrc(raw, '160x160');
    var url   = HF.buildPdpUrl(handle, productData);
    var safe  = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    var imgHtml = img
      ? '<img class="hf-alt-thumb" src="' + img + '" alt="' + title.replace(/"/g, '&quot;') + '" loading="lazy">'
      : '<div class="hf-alt-thumb hf-alt-thumb--empty"></div>';

    return '<li class="hf-alt-card">' + imgHtml + '<span class="hf-alt-title">' + title + '</span><button class="hf-alt-cta" data-pdp-link="' + safe + '">View &#8594;</button></li>';
  };

  /* ------------------------------------------------------------------
     PHASE 4 — RESULTS TERMINAL
     ------------------------------------------------------------------ */

  HF.renderTerminal = async function () {
    HF.els.body.innerHTML = '<div class="hf-loading"><p>Finding your holsters...</p></div>';
    HF.updateBackBtn();

    var candidates = HF.state.candidates;
    if (!candidates.length) {
      HF.renderErrorState(
        'No matching holsters found. Try going back and adjusting your selections, or ' +
        '<a href="/collections/allholsters">browse all holsters</a>.'
      );
      return;
    }

    var productDataArr = await Promise.all(
      candidates.map(function (h) {
        return HF.fetchProductData(h).then(function (data) { return { handle: h, data: data }; });
      })
    );

    var heroHandle = HF.pickHero();
    var alts = candidates.filter(function (h) { return h !== heroHandle; });

    HF.transitionBody(function () {
      var stepTotal = HF.state.history.length;
      HF.renderProgress(stepTotal, stepTotal); // all dots done

      var html = ['<div class="hf-results">'];
      html.push(HF.buildAnswerSummary());

      var heroData = productDataArr.find(function (r) { return r.handle === heroHandle; });
      html.push(HF.buildHeroCardHtml(heroHandle, heroData ? heroData.data : null));

      if (alts.length) {
        html.push('<p class="hf-alts-label">Other Great Options:</p>');
        html.push('<ul class="hf-alt-grid">');
        alts.forEach(function (h) {
          var r = productDataArr.find(function (r2) { return r2.handle === h; });
          html.push(HF.buildAltCardHtml(h, r ? r.data : null));
        });
        html.push('</ul>');
      }

      html.push('</div>');
      HF.els.body.innerHTML = html.join('\n');

      HF.els.body.querySelectorAll('[data-pdp-link]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          window.location.href = btn.getAttribute('data-pdp-link');
        });
      });
    });
  };

  /* ------------------------------------------------------------------
     ERROR STATE
     ------------------------------------------------------------------ */
  HF.renderErrorState = function (message) {
    if (!HF.els.body) return;
    HF.els.body.innerHTML =
      '<div class="hf-error">' +
      '<p>' + (message || 'Something went wrong. Please try again.') + '</p>' +
      '<p><a href="/collections/allholsters">Browse all holsters</a></p>' +
      '</div>';
  };

  /* ------------------------------------------------------------------
     BOOT
     ------------------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', HF.init);
  } else {
    HF.init();
  }
})();
